import { Link, NavLink } from 'react-router';
import { Lock, LogIn, LogOut, User } from 'lucide-react';
import { useAuth, type AuthUser } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import {
  HoverableDropdown,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface HeaderProps {
  /** 'public' on landing/login; 'dashboard' inside admin/affiliate areas */
  variant?: 'public' | 'dashboard';
  /** For dashboard: which role section we're in */
  role?: 'admin' | 'affiliate';
}

interface NavLink {
  to: string;
  label: string;
}

const adminDropdownLinks: NavLink[] = [
  { to: '/admin', label: 'Panel de Control' },
  { to: '/admin/affiliates', label: 'Creadores' },
  { to: '/admin/applications', label: 'Postulaciones' },
  { to: '/admin/conversions', label: 'Conversiones' },
  { to: '/admin/conversions-report', label: 'Reporte' },
  { to: '/admin/courses', label: 'Cursos' },
  { to: '/admin/payouts', label: 'Pagos' },
  { to: '/admin/contabilidad', label: 'Contabilidad' },
  { to: '/admin/users', label: 'Equipo' },
  { to: '/admin/settings', label: 'Configuración' },
  { to: '/admin/audit', label: 'Logs' },
  { to: '/admin/documents', label: 'Documentos' },
];

const adminTopNavLinks: NavLink[] = adminDropdownLinks.filter(
  (l) =>
    l.to !== '/admin/settings' &&
    l.to !== '/admin/audit' &&
    l.to !== '/admin/users',
);

const affiliateLinks: NavLink[] = [
  { to: '/affiliate', label: 'Panel de Control' },
  { to: '/affiliate/conversions', label: 'Conversiones' },
  { to: '/affiliate/payouts', label: 'Pagos' },
  { to: '/affiliate/profile', label: 'Perfil' },
];

function dropdownLinksForRole(role: AuthUser['role']): NavLink[] {
  return role === 'admin' ? adminDropdownLinks : affiliateLinks;
}

function topNavLinksForRole(role: AuthUser['role']): NavLink[] {
  return role === 'admin' ? adminTopNavLinks : affiliateLinks;
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return cn(
    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary-light text-primary-dark'
      : 'text-muted-foreground hover:bg-primary-light hover:text-foreground',
  );
}

function NavBar({ links }: { links: NavLink[] }) {
  return (
    <nav className="flex items-center gap-1">
      {links.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.to === '/admin' || link.to === '/affiliate'}
          className={navLinkClass}
        >
          {link.label}
        </NavLink>
      ))}
    </nav>
  );
}

interface UserMenuProps {
  user: AuthUser;
  onLogout: () => void;
  /** Nav links to render inside the dropdown (one entry per role-aware destination). */
  dropdownLinks: NavLink[];
}

function UserMenu({ user, onLogout, dropdownLinks }: UserMenuProps) {
  const trigger = (
    <button
      className="flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-primary-light hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:bg-primary-light data-[state=open]:text-primary-dark"
      aria-label="Menú de usuario"
    >
      <User className="h-4 w-4" />
    </button>
  );

  return (
    <HoverableDropdown
      contentClassName="min-w-[14rem]"
      trigger={trigger}
    >
      <DropdownMenuLabel className="text-xs text-muted-foreground">
        Sesión iniciada como
      </DropdownMenuLabel>
      <DropdownMenuItem asChild>
        <Link to={dropdownLinks[0]?.to ?? '/'} className="cursor-pointer font-medium">
          {user.name}
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {dropdownLinks.map((link) => (
        <DropdownMenuItem key={link.to} asChild>
          <Link to={link.to} className="cursor-pointer">
            {link.label}
          </Link>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      {/* Cambio de contraseña: ruta agnóstica del role, el
          endpoint /auth/set-password branchea en el backend. */}
      <DropdownMenuItem asChild>
        <Link to="/set-password" className="cursor-pointer">
          <Lock className="mr-2 h-4 w-4" />
          Cambiar contraseña
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={() => void onLogout()}
        className="cursor-pointer text-muted-foreground"
      >
        <LogOut className="mr-2 h-4 w-4" />
        Cerrar sesión
      </DropdownMenuItem>
    </HoverableDropdown>
  );
}

export function Header({ variant = 'public', role }: HeaderProps) {
  const { user, loading, logout } = useAuth();

  const showDashboardNav = variant === 'dashboard' && Boolean(role);
  const navLinks = showDashboardNav && role ? topNavLinksForRole(role) : [];
  const dropdownLinks = user ? dropdownLinksForRole(user.role) : [];

  return (
    <header className="border-b border-border bg-surface">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center" aria-label="Red de creadores de contenido Educrea — Inicio">
          <picture>
            <source media="(max-width: 640px)" srcSet="/logo-vertical.webp" />
            <img
              src="/logo-horizontal.webp"
              alt="Red de creadores de contenido Educrea"
              className="h-10 w-auto sm:h-12"
            />
          </picture>
        </Link>

        {showDashboardNav && navLinks.length > 0 && <NavBar links={navLinks} />}

        <div className="flex items-center gap-2">
          {loading ? null : user ? (
            <UserMenu user={user} onLogout={logout} dropdownLinks={dropdownLinks} />
          ) : (
            <Button asChild variant="outline" size="sm">
              <Link to="/login">
                <LogIn className="h-4 w-4" />
                Ingresar
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
